import { useEffect, useState, useMemo } from "react";
import { T } from "@/components/T";
import { useSearchParams, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchGlossary } from "@/lib/api";
import { itemPath, CATEGORY_BY_TYPE } from "@/lib/categories";
import { MandalaMotif } from "@/components/MotifBackground";
import { Search } from "lucide-react";

export default function Glossary() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const [terms, setTerms] = useState([]);
  const [q, setQ] = useState(params.get("q") || "");

  const loc = (term) => ({
    meaning: (term.meaning_i18n || {})[lang] || term.meaning,
    context: (term.context_i18n || {})[lang] || term.context,
  });

  useEffect(() => {
    fetchGlossary().then(setTerms).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    if (!q.trim()) return terms;
    const s = q.trim().toLowerCase();
    return terms.filter(
      (term) =>
        term.devanagari.includes(q.trim()) ||
        (term.transliteration || "").toLowerCase().includes(s) ||
        (term.meaning || "").toLowerCase().includes(s)
    );
  }, [q, terms]);

  return (
    <>
      <section className="relative overflow-hidden border-b border-line bg-[var(--surface)]">
        <MandalaMotif className="pointer-events-none absolute -right-16 -top-8 h-72 w-72 text-[var(--gold)] opacity-[0.07]" />
        <div className="relative z-[2] mx-auto max-w-5xl px-5 py-16 md:px-8 md:py-20">
          <p className="overline-label mb-3"><T>Discovery</T></p>
          <h1 className="font-deva text-5xl text-ink deva-lh md:text-6xl">शब्दकोश</h1>
          <p className="font-serif-latin text-2xl text-ink-2"><T>Bharatiya Knowledge Glossary</T></p>
          <p className="mt-4 max-w-2xl leading-relaxed text-ink-2">
            <T>Sanskrit and Bharatiya terms that recur across the tradition — with transliteration, simple meaning and context.</T>
          </p>
          <div className="mt-8 flex max-w-lg items-center gap-3 border-b-2 border-line pb-2 transition-colors focus-within:border-saffron">
            <Search className="h-5 w-5 text-ink-3" />
            <input
              data-testid="glossary-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search a term…"
              className="w-full bg-transparent py-2 font-deva-body text-lg text-ink placeholder:text-ink-3 focus:outline-none"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-5 py-14 md:px-8 md:py-16">
        {filtered.length === 0 ? (
          <p className="py-16 text-center text-ink-3">{t("no_results")}</p>
        ) : (
          <div className="grid gap-5 sm:grid-cols-2">
            {filtered.map((term) => {
              const { meaning, context } = loc(term);
              return (
              <div key={term.id} data-testid={`glossary-term-${term.id}`} className="border border-line bg-surface p-6">
                <div className="flex items-baseline gap-3">
                  <span className="font-deva text-3xl text-ink">{term.devanagari}</span>
                  <span className="text-gold">— {term.transliteration}</span>
                </div>
                <p className="mt-3 leading-relaxed text-ink-2">{meaning}</p>
                {context && <p className="mt-2 text-sm italic leading-relaxed text-ink-3">{context}</p>}
                {term.related?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-2">
                    {term.related.map((slug) => (
                      <RelatedChip key={slug} slug={slug} />
                    ))}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </section>
    </>
  );
}

function RelatedChip({ slug }) {
  // Guess path from slug prefix using known types; fallback to a safe default.
  const guessType = slug.startsWith("katha") ? "katha" : slug.includes("puja") ? "puja_vidhi" : slug.includes("mantra") ? "mantra" : slug.includes("stotra") || slug.includes("ashtak") ? "stotra" : "sukta";
  const cat = CATEGORY_BY_TYPE[guessType] || CATEGORY_BY_TYPE.sukta || CATEGORY_BY_TYPE.katha;
  if (!cat) return null;
  return (
    <Link to={`${cat.path}/${slug}`} className="border border-line px-3 py-1 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">
      {slug.replace(/-/g, " ")}
    </Link>
  );
}
